import isEqual from "lodash.isequal";
import React, { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { ButtonGroupContext } from ".";
import type { ButtonGroupContextType } from ".";

export type ButtonProps<T> = {
  value: T;
  label: string;
  buttonClassName?: string;
  selectedButtonClassName?: string;
  renderOption?: ({ option, isSelected }: { option: ButtonOption<T>; isSelected: boolean }) => React.ReactNode;
};

type ButtonOption<T> = {
  value: T;
  label: string;
};

const ButtonGroupButton = <T,>({
  value,
  label,
  buttonClassName,
  selectedButtonClassName,
  renderOption,
}: ButtonProps<T>) => {
  const context = React.useContext(ButtonGroupContext) as ButtonGroupContextType<T>;

  if (!context) {
    throw new Error("ButtonGroup.Button must be used within a ButtonGroup");
  }

  const { selectedOption, onOptionChange, onKeyDown } = context;

  const isSelected = isEqual(selectedOption, value);

  const buttonClassNames = useMemo(
    () => (isSelected ? twMerge("bg-white shadow-xs font-medium", selectedButtonClassName) : buttonClassName),
    [isSelected, buttonClassName, selectedButtonClassName],
  );

  return (
    <button
      type="button"
      onClick={() => onOptionChange(value)}
      onKeyDown={onKeyDown}
      className={twMerge(
        "flex justify-center items-center text-gray-900 font-normal px-3 py-2 rounded leading-none tracking-[-0.28px] text-sm transition-colors ease-in-out flex-grow",
        buttonClassNames,
      )}
      aria-pressed={isSelected}
    >
      {renderOption ? renderOption({ option: { value, label }, isSelected }) : label}
    </button>
  );
};

export default ButtonGroupButton;
